/**
 * identity v2 — сервис пользователей второй версии.
 *
 * Зачем этот файл. v1 (`/api-user/api/v1/users/*`) отключается, и весь
 * Рублокс переезжает сюда. Модуль написан строго по openapi-контракту
 * сервиса: https://minecraftia-school.ru/api/v2/identity/openapi.json
 * (45 эндпоинтов, схема открыта — при сомнениях сверяться с ней, а не
 * с этим файлом).
 *
 * ⚠️ ГЛАВНОЕ ОТЛИЧИЕ ОТ v1: идентификатор пользователя — UUID
 * («019f73cc-e49f-7f60-bd32-5939977f2607»), а не число. `GET /users/{ident}`
 * принимает ТОЛЬКО UUID — проверено: по числовому id и по нику отвечает
 * «Пользователь не найден». Поэтому старые ссылки вида /app/profile/1
 * без конвертации не откроются (мост — на нашем бэкенде, см. Э2 плана
 * в RUBLOX_V2_IDENTITY_MIGRATION.md).
 *
 * Файл НЕ подменяет api/auth.js: переключение будет отдельным шагом,
 * чтобы был откат. Пока это параллельный слой.
 */

import { ApiError } from "./client";

export const IDENTITY_BASE = "https://minecraftia-school.ru/api/v2/identity";

/* Ключи хранилища те же, что у v1. Менять нельзя: плеер и студия читают
   localStorage['Authorization'] напрямую — переименование ключа отвяжет
   их от сессии сайта. */
const TOKEN_KEY = "rublox_token";
const REFRESH_KEY = "rublox_refresh";

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getRefresh() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/**
 * Сохранить пару токенов.
 *
 * `Authorization` дублируется намеренно: плеер и студия живут на других
 * доменах и читают именно этот ключ. Формат — СЫРОЙ JWT без «Bearer»:
 * authgate срезает префикс опционально, но плеер этого не делает.
 */
export function setTokens(access, refresh) {
  try {
    if (access) {
      localStorage.setItem(TOKEN_KEY, access);
      localStorage.setItem("Authorization", access);
    }
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {
    /* приватный режим */
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem("Authorization");
  } catch {
    /* приватный режим */
  }
}

/* Человекочитаемые сообщения. Сервис отвечает либо строкой в `detail`,
   либо массивом ошибок валидации FastAPI — их нельзя показывать как есть:
   «value is not a valid email address: The part after the @-sign…» ребёнок
   не прочитает. */
function humanMessage(status, data) {
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) {
    const field = d[0]?.loc?.[d[0].loc.length - 1];
    const byField = {
      email: "Проверь адрес почты — похоже, в нём опечатка.",
      password: "Пароль не подходит: нужно минимум 8 символов.",
      nickname: "Ник не подходит. Попробуй другой.",
      refresh_token: "Сессия устарела. Войди заново.",
    };
    return byField[field] || "Проверь заполненные поля.";
  }
  if (status === 401) return "Нужно войти заново.";
  if (status === 403) return "Недостаточно прав.";
  if (status === 404) return "Не найдено.";
  if (status === 429) return "Слишком много попыток. Подожди минуту.";
  if (status >= 500) return "Сервер временно недоступен. Попробуй позже.";
  return "Не удалось выполнить запрос.";
}

/* Единственный «полёт» обновления токена на всё приложение: если сразу
   пять запросов получат 401, обновление должно произойти один раз, иначе
   параллельные refresh взаимно инвалидируют друг друга. */
let refreshInFlight = null;

async function doRefresh() {
  const refresh_token = getRefresh();
  if (!refresh_token) return false;
  try {
    const res = await fetch(`${IDENTITY_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token }),
    });
    if (!res.ok) {
      // Сервер ЯВНО отверг refresh — сессия мертва, чистим.
      if (res.status === 401 || res.status === 403 || res.status === 422) {
        clearTokens();
      }
      return false;
    }
    const data = await res.json();
    if (!data?.access_token) return false;
    setTokens(data.access_token, data.refresh_token || refresh_token);
    return true;
  } catch {
    /* Сеть отвалилась — НЕ чистим токены: иначе каждый обрыв связи
       разлогинивал бы человека насовсем. */
    return false;
  }
}

export function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Запрос к identity v2.
 *
 * При 401 один раз обновляет токен и повторяет — ровно один раз, иначе
 * при мёртвом refresh получился бы бесконечный цикл.
 */
export async function idn(path, options = {}) {
  const {
    method = "GET",
    body,
    auth = true,
    signal,
    _retried = false,
  } = options;

  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getAccessToken();
    if (token) headers.Authorization = token;
  }

  let res;
  try {
    res = await fetch(IDENTITY_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError("Не удалось связаться с сервером. Проверь интернет.", 0);
  }

  if (res.status === 401 && auth && !_retried && getRefresh()) {
    const ok = await refreshOnce();
    if (ok) return idn(path, { ...options, _retried: true });
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* 204 и пустые ответы — это норма */
  }

  if (!res.ok) {
    throw new ApiError(humanMessage(res.status, data), res.status);
  }
  return data;
}

/* ── Авторизация ────────────────────────────────────────────────── */

/** Вход. `{email, password}` → сохраняет пару токенов и возвращает профиль. */
export async function login(email, password) {
  const t = await idn("/auth/login", {
    method: "POST",
    auth: false,
    body: { email, password },
  });
  setTokens(t.access_token, t.refresh_token);
  return me();
}

/**
 * Регистрация. Обязательны email, password, nickname.
 * `grade` и `coding_exp` опциональны — в v1 их не было, и форма Рублокса
 * их не собирает; передаём только если пришли.
 */
export async function signup({ email, password, nickname, grade, codingExp }) {
  const body = { email, password, nickname };
  if (grade != null) body.grade = grade;
  if (codingExp != null) body.coding_exp = codingExp;
  const t = await idn("/auth/signup", { method: "POST", auth: false, body });
  setTokens(t.access_token, t.refresh_token);
  return me();
}

/** Выход. Токены чистим в любом случае — даже если сервер не ответил:
    остаться «наполовину вошедшим» хуже, чем лишний мёртвый токен на бэке. */
export async function logout() {
  try {
    await idn("/auth/logout", { method: "POST" });
  } catch {
    /* уже неважно */
  } finally {
    clearTokens();
  }
}

/** Выйти на всех устройствах. */
export async function logoutAll() {
  try {
    await idn("/auth/logout-all", { method: "POST" });
  } finally {
    clearTokens();
  }
}

/** Кто я. Единственный источник личности пользователя в v2. */
export function me(signal) {
  return idn("/me", { signal });
}

/** Активные сессии (устройства). В v1 такого не было. */
export function getSessions(signal) {
  return idn("/auth/sessions", { signal });
}

export function revokeSession(sid) {
  return idn(`/auth/sessions/${encodeURIComponent(sid)}/revoke`, {
    method: "POST",
  });
}

/* ── Яндекс OAuth ───────────────────────────────────────────────────
   ⚠️ Google в v2 НЕТ — в схеме только Яндекс. Кнопку Google нужно либо
   убрать, либо дождаться поддержки на бэкенде (вопрос вынесен в план). */

export function yandexStartUrl() {
  return `${IDENTITY_BASE}/auth/yandex/start`;
}

export async function yandexExchange(code, state) {
  const t = await idn("/auth/yandex/exchange", {
    method: "POST",
    auth: false,
    body: { code, state },
  });
  setTokens(t.access_token, t.refresh_token);
  return me();
}

/* ── Профиль ────────────────────────────────────────────────────── */

export function setNickname(nickname) {
  return idn("/profile/nickname", { method: "POST", body: { nickname } });
}

export function setUsername(username) {
  return idn("/profile/username", { method: "POST", body: { username } });
}

export function usernameAvailable(username, signal) {
  return idn(`/profile/username/available?username=${encodeURIComponent(username)}`, {
    signal,
  });
}

export function setGrade(grade) {
  return idn("/profile/set-grade", { method: "POST", body: { grade } });
}

export function setPassword(newPassword, oldPassword) {
  return idn("/profile/set-password", {
    method: "POST",
    body: { new_password: newPassword, old_password: oldPassword },
  });
}

export function passwordStatus(signal) {
  return idn("/profile/password-status", { signal });
}

export function getLevels(signal) {
  return idn("/profile/levels", { signal });
}

export function buyLevel() {
  return idn("/profile/buy-level", { method: "POST" });
}

/** Профили пачкой — для списков (друзья, комментарии, лидерборд). */
export function profileBatch(ids, signal) {
  return idn("/profile/batch", { method: "POST", body: { ids }, signal });
}

/**
 * Загрузка аватара/обложки. multipart, поэтому мимо idn():
 * Content-Type должен выставить браузер вместе с boundary.
 */
async function uploadFile(path, file, field = "file") {
  const fd = new FormData();
  fd.append(field, file);
  const token = getAccessToken();
  const res = await fetch(IDENTITY_BASE + path, {
    method: "POST",
    headers: token ? { Authorization: token } : {},
    body: fd,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* пусто */
  }
  if (!res.ok) throw new ApiError(humanMessage(res.status, data), res.status);
  return data;
}

export function uploadAvatar(file) {
  return uploadFile("/profile/avatar", file);
}

export function uploadCover(file) {
  return uploadFile("/profile/cover", file);
}

/* ── Пользователи ───────────────────────────────────────────────────
   ⚠️ `ident` — ТОЛЬКО UUID. Проверено на живом API: числовой v1-id и ник
   дают «Пользователь не найден». Конвертация старых ссылок — через наш
   бэкенд (у него есть доступ к internal-эндпоинтам с секретом). */

export function getUser(uuid, signal) {
  return idn(`/users/${encodeURIComponent(uuid)}`, { signal });
}

export function listUsers(signal) {
  return idn("/users", { signal, auth: false });
}

/* ── Санкции и подписка ─────────────────────────────────────────── */

export function mySanctions(signal) {
  return idn("/me/sanctions", { signal });
}

export function mySubscription(signal) {
  return idn("/me/subscription", { signal });
}

export function setAutoRenew(enabled) {
  return idn("/me/subscription/auto-renew", {
    method: "PUT",
    body: { auto_renew: !!enabled },
  });
}

/* ── SSO: плеер и студия ────────────────────────────────────────── */

/** Одноразовый тикет для перехода в плеер/студию/школу. */
export async function playTicket() {
  const d = await idn("/sso/play-ticket", { method: "POST" });
  return d?.ticket || d;
}

/** Обмен тикета на сессию (вызывает принимающая сторона). */
export async function redeemTicket(ticket) {
  const t = await idn("/sso/redeem-ticket", {
    method: "POST",
    auth: false,
    body: { ticket },
  });
  if (t?.access_token) setTokens(t.access_token, t.refresh_token);
  return t;
}

/** Продление токена по SSO (без пароля). */
export async function renewToken() {
  const t = await idn("/sso/renew-token", { method: "POST" });
  if (t?.access_token) setTokens(t.access_token, t.refresh_token);
  return t;
}

/** Токен для Маськи (кросс-продуктовый переход). */
export function maskaToken() {
  return idn("/maska/token", { method: "POST" });
}

/* ── Мост старых ссылок ─────────────────────────────────────────────
   По всему Рублоксу ссылки на профили выглядят как /app/profile/1 —
   это v1-id. В v2 такой профиль не найдётся: `/users/{ident}` принимает
   только UUID. Конвертацию делает НАШ бэкенд (`/identity/resolve`):
   у него есть X-Internal-Secret, которого нет у браузера.

   Кэш в памяти: на странице профиля ссылка разрешается один раз, а не
   на каждый ре-рендер. */

const RUBLOX_API = "https://api.rublox.pro/api/v2/rublox";
const _uuidCache = new Map();

/** UUID → числовой v1-id. Нужно для storys: его маршруты объявлены
    как <int:user_id>, и вся база привязана к числовому владельцу.
    Число на входе возвращается как есть. */
export async function resolveV1Id(accountId, signal) {
  const value = String(accountId || "");
  if (!value) return null;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return value;
  }
  if (_v1Cache.has(value)) return _v1Cache.get(value);
  try {
    const res = await fetch(
      `${RUBLOX_API}/identity/resolve?account_id=${encodeURIComponent(value)}`,
      { signal }
    );
    if (!res.ok) {
      if (res.status === 404) _v1Cache.set(value, null);
      return null;
    }
    const data = await res.json();
    const v1 = data?.v1_id || null;
    _v1Cache.set(value, v1);
    return v1;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    return null;
  }
}

const _v1Cache = new Map();

/** Число (v1-id) → UUID. UUID возвращается как есть. null, если связки нет. */
export async function resolveAccountId(id, signal) {
  const value = String(id || "");
  if (!value) return null;
  // Уже UUID — конвертировать нечего.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return value;
  }
  if (_uuidCache.has(value)) return _uuidCache.get(value);

  try {
    const res = await fetch(
      `${RUBLOX_API}/identity/resolve?v1_id=${encodeURIComponent(value)}`,
      { signal }
    );
    if (!res.ok) {
      // 404 = связки нет. Кэшируем, чтобы не спрашивать повторно:
      // у 11 из 637 владельцев сейвов аккаунта v2 действительно нет.
      if (res.status === 404) _uuidCache.set(value, null);
      return null;
    }
    const data = await res.json();
    const uuid = data?.account_id || null;
    _uuidCache.set(value, uuid);
    return uuid;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    // Сеть моргнула — НЕ кэшируем отрицательный ответ: ссылка рабочая,
    // просто сейчас не смогли спросить.
    return null;
  }
}
